import { useEffect, useRef, useState } from "react"
import { getCurrentTime, useCurrentTime } from "../../../hooks/useCurrentTime"
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux"
import {
    getTimelineViewportElement,
    getTimelineViewportMetrics,
    scrollTimelineBy,
    scrollTimelineTo,
} from "../../../hooks/useTimelineViewport"
import { setTimeToSeek } from "../../../store/slices/masterTimeline.slice"
import { LineSliderInput } from "../../../style/lineSliderInput"
import { TIMELINE_ROW } from "../../../style/sizing"

interface Props {
    thumbHeight: number
    handleChange: (val: number) => void
}

/** geometry that only changes on resize — never re-measured per frame */
type SliderMetrics = {
    /** slider offset inside the scrollable content of the timeline container */
    sliderContentLeft: number
    sliderWidth: number
    /** viewport x of the timeline container's left edge */
    containerViewportLeft: number
    /** window width minus the right-hand object tools and the row heading */
    scrollOffset: number
}

const CustomLineSlider = ({ thumbHeight, handleChange }: Props) => {
    const dispatch = useAppDispatch()
    // the playhead time is the only thing here that changes every frame — subscribing to it
    // in this leaf keeps the rest of the timeline out of the per-frame render path
    const time = useCurrentTime()
    const [isDragging, setIsDragging] = useState(false)
    const sliderRef = useRef<HTMLInputElement>(null)
    const isPlaying = useAppSelector((state) => state.masterTimeline.isPlaying)

    const masterTimeline = useAppSelector((state) => state.masterTimeline.value)
    const duration = masterTimeline?.duration() || 0

    const value = (time / duration) * 100 || 0

    const handleMouseDown = () => setIsDragging(true)

    // The release has to be caught globally. onMouseUp on the input alone misses every drag
    // that ends outside it — which is most of them, since the playhead is 3px wide — leaving
    // the slider stuck in its dragging state and never committing the new time.
    useEffect(() => {
        if (!isDragging) return

        const stopDragging = () => {
            dispatch(setTimeToSeek(getCurrentTime()))
            setIsDragging(false)
        }

        window.addEventListener("mouseup", stopDragging)
        window.addEventListener("touchend", stopDragging)
        return () => {
            window.removeEventListener("mouseup", stopDragging)
            window.removeEventListener("touchend", stopDragging)
        }
    }, [isDragging, dispatch])

    const metricsRef = useRef<SliderMetrics>({
        sliderContentLeft: 0,
        sliderWidth: 0,
        containerViewportLeft: 0,
        scrollOffset: 0,
    })

    // Measure once on mount and whenever something resizes. Previously every one of these
    // reads happened inside an effect that ran on each animation frame, interleaved with
    // `scrollLeft` writes — textbook layout thrashing.
    useEffect(() => {
        const measure = () => {
            const slider = sliderRef.current
            const timelineContainer = getTimelineViewportElement()
            if (!slider || !timelineContainer) return

            const objectTools = document.querySelector(".object-tools")
            const sliderRect = slider.getBoundingClientRect()
            const containerRect = timelineContainer.getBoundingClientRect()

            metricsRef.current = {
                sliderContentLeft:
                    sliderRect.left - containerRect.left + timelineContainer.scrollLeft,
                sliderWidth: sliderRect.width,
                containerViewportLeft: containerRect.left,
                scrollOffset:
                    window.innerWidth - (objectTools?.clientWidth ?? 0) - TIMELINE_ROW.headingWidth,
            }
        }

        measure()

        const observer = new ResizeObserver(measure)
        if (sliderRef.current) observer.observe(sliderRef.current)
        const timelineContainer = getTimelineViewportElement()
        if (timelineContainer) observer.observe(timelineContainer)
        const objectTools = document.querySelector(".object-tools")
        if (objectTools) observer.observe(objectTools)
        window.addEventListener("resize", measure)

        return () => {
            observer.disconnect()
            window.removeEventListener("resize", measure)
        }
    }, [])

    // read by the rAF callback so a frame queued earlier still acts on the newest values
    const latestRef = useRef({ value, isPlaying })
    latestRef.current = { value, isPlaying }

    const rafRef = useRef(0)

    useEffect(() => {
        if (rafRef.current) return // at most one evaluation per frame
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0

            const { value: currentValue, isPlaying: playing } = latestRef.current
            const metrics = metricsRef.current
            if (!metrics.sliderWidth) return // not measured yet

            const { scrollLeft, clientWidth } = getTimelineViewportMetrics()

            // arithmetic instead of getBoundingClientRect(): the thumb sits at `value`%
            // of the slider, whose geometry only changes on resize
            const thumbContentX =
                metrics.sliderContentLeft + (currentValue / 100) * metrics.sliderWidth
            const thumbRelativeToContainer = thumbContentX - scrollLeft
            const thumbX = metrics.containerViewportLeft + thumbRelativeToContainer

            /**
             * CASE 1: When playing the animation and the thumb moves outside the visible area (right side),
             * scroll the container to keep it ~2/3 into view.
             */
            if (playing && thumbRelativeToContainer > clientWidth) {
                scrollTimelineBy((metrics.scrollOffset / 3) * 2)
            }

            /**
             * CASE 2: When playing and the thumb is completely off-screen to the left,
             * reset scroll to the beginning.
             */
            if (playing && thumbX <= 0 && scrollLeft > 0) {
                scrollTimelineTo(0)
            }

            /**
             * CASE 3: When manually dragging (not playing),
             * ensure the thumb stays within the visible viewport.
             * Scroll in smaller fixed amounts to keep thumb in view.
             */
            if (!playing && thumbRelativeToContainer > clientWidth) {
                scrollTimelineBy(40)
            }
            if (!playing && thumbRelativeToContainer < TIMELINE_ROW.headingWidth) {
                scrollTimelineBy(-40)
            }
        })
    }, [value, isPlaying])

    useEffect(
        () => () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        },
        []
    )

    return (
        <LineSliderInput
            ref={sliderRef}
            type="range"
            value={value}
            disabled={duration === 0}
            min={0}
            max={100}
            step={0.01}
            onChange={(e) => handleChange(Number(e.target.value))}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            isDisabled={duration === 0}
            rangeValue={value}
            thumbHeight={thumbHeight}
        />
    )
}

export default CustomLineSlider
